export type Discount = {
  type: 'percentage' | 'amount' | 'final_price';
  value: number;
};

/** Round half-up to 2 decimal places */
function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

export function calculatePartTotal(
  quantity: number,
  unitPrice: number,
  discount?: Discount
): { discountAmount: number; discountedUnitPrice: number; total: number } {
  if (quantity === 0 || unitPrice === 0) {
    return { discountAmount: 0, discountedUnitPrice: unitPrice, total: 0 };
  }

  const grossTotal = round2(quantity * unitPrice);

  if (!discount) {
    return { discountAmount: 0, discountedUnitPrice: unitPrice, total: grossTotal };
  }

  let total: number;
  let discountAmount: number;

  switch (discount.type) {
    case 'percentage':
      discountAmount = round2((unitPrice * discount.value) / 100);
      total = round2(quantity * (unitPrice - discountAmount));
      break;
    case 'amount':
      discountAmount = round2(discount.value);
      total = round2(grossTotal - discountAmount);
      break;
    case 'final_price':
      total = round2(discount.value);
      discountAmount = round2(grossTotal - total);
      break;
    default:
      return { discountAmount: 0, discountedUnitPrice: unitPrice, total: grossTotal };
  }

  const discountedUnitPrice = round2(total / quantity);

  return { discountAmount, discountedUnitPrice, total };
}

export function calculateLaborTotal(
  hours: number,
  rate: number,
  discount?: Discount
): { discountAmount: number; total: number } {
  const grossTotal = round2(hours * rate);

  if (!discount) {
    return { discountAmount: 0, total: grossTotal };
  }

  let total: number;
  let discountAmount: number;

  switch (discount.type) {
    case 'percentage':
      discountAmount = round2((grossTotal * discount.value) / 100);
      total = round2(grossTotal - discountAmount);
      break;
    case 'amount':
      discountAmount = round2(discount.value);
      total = round2(grossTotal - discountAmount);
      break;
    case 'final_price':
      total = round2(discount.value);
      discountAmount = round2(grossTotal - total);
      break;
    default:
      return { discountAmount: 0, total: grossTotal };
  }

  return { discountAmount, total };
}

export function calculateLineTotal(laborTotal: number, partsTotal: number): number {
  return round2(laborTotal + partsTotal);
}

export function calculateDocumentTotal(
  subtotal: number,
  taxRate: number,
  wholeJobDiscount?: Discount
): { discountAmount: number; taxAmount: number; total: number } {
  let discountAmount = 0;
  let taxableAmount = subtotal;

  if (wholeJobDiscount) {
    switch (wholeJobDiscount.type) {
      case 'percentage':
        discountAmount = round2((subtotal * wholeJobDiscount.value) / 100);
        break;
      case 'amount':
        discountAmount = round2(wholeJobDiscount.value);
        break;
      case 'final_price':
        discountAmount = round2(subtotal - wholeJobDiscount.value);
        break;
    }

    if (discountAmount > subtotal) {
      throw new Error('Discount cannot exceed subtotal');
    }
    // A negative discount must never become a surcharge that inflates the bill.
    if (discountAmount < 0) {
      discountAmount = 0;
    }

    taxableAmount = round2(subtotal - discountAmount);
  }

  const taxAmount = round2((taxableAmount * taxRate) / 100);
  const total = round2(taxableAmount + taxAmount);

  return { discountAmount, taxAmount, total };
}

export function calculateSavings(
  originalPrice: number,
  finalPrice: number
): { savedAmount: number; savedPercentage: number } {
  const savedAmount = round2(originalPrice - finalPrice);
  const savedPercentage = originalPrice > 0 ? round2((savedAmount / originalPrice) * 100) : 0;

  return { savedAmount, savedPercentage };
}
